import { useCallback, useMemo, useState } from 'react';
import type { InputPanelRoleItem } from '$/components/InputPanelToolBar/types';
import { mapMyIpToInputPanelRole } from '$/components/InputPanelToolBar/ipSelection';
import type { MyIpItem } from '$/domains/myIp/types';
import { useMyIpPickerList } from '$/hooks/myIp/useMyIpAssetList';
import { useMyIpSession } from '@/hooks/myIp/useMyIpSession';

const emptyItems: MyIpItem[] = [];

/** 两类 IP 共享 picker 的鉴权、分页及失效语义；不预取全库，分页错误只重试失败页。 */
function useInputIpRoleScope(
  scopeKey: string,
  enabled: boolean,
  scope: InputPanelRoleItem['scope'],
  context: { projectId?: string; roomId?: string; },
) {
  const state = useMyIpPickerList(scopeKey, enabled, scope, context);
  // SDK 的隔离空态可能生成新数组；稳定空集合避免首页同步 roleList 时发生重复更新。
  const items = state.list.length ? state.list : emptyItems;
  const roles = useMemo(() => items.map(mapMyIpToInputPanelRole), [items]);
  return {
    roles,
    status: state.status,
    error: state.error,
    loadingMore: state.loadingMore,
    hasMore: state.hasMore,
    retry: state.status === 'success' && state.error ? state.loadMore : state.refresh,
    loadMore: state.loadMore,
  };
}

type InputIpRolesOptions = {
  source?: 'home' | 'project';
  projectId?: string;
  roomId?: string;
  cacheAfterClose?: boolean;
};

/** 输入框在角色弹框展开后才读取 picker；Home 保留结果，房间关闭后释放当次列表。 */
export function useInputIpRoles({
  source = 'home',
  projectId,
  roomId,
  cacheAfterClose = true,
}: InputIpRolesOptions = {}) {
  const session = useMyIpSession();
  const [rolePopupOpen, setRolePopupOpen] = useState(false);
  const [cachedRequestKey, setCachedRequestKey] = useState('');
  const contextReady = source === 'home' || Boolean(projectId && roomId);
  const enabled = session.enabled && contextReady;
  const requestKey = JSON.stringify([session.scopeKey, source, projectId, roomId]);
  const requestEnabled = enabled && (cacheAfterClose
    ? cachedRequestKey === requestKey
    : rolePopupOpen);
  const context = {
    projectId: source === 'project' ? projectId : undefined,
    roomId: source === 'project' ? roomId : undefined,
  };
  const personal = useInputIpRoleScope(session.scopeKey, requestEnabled, 'personal', context);
  const official = useInputIpRoleScope(session.scopeKey, requestEnabled, 'official', context);
  const roles = useMemo(() => [...personal.roles, ...official.roles], [personal.roles, official.roles]);

  /** Home 首次展开后保留当前身份结果；房间则直接跟随弹框开关。 */
  const handleRolePopupOpenChange = useCallback((open: boolean) => {
    setRolePopupOpen(open);
    if (open && cacheAfterClose) setCachedRequestKey(requestKey);
  }, [cacheAfterClose, requestKey]);

  return {
    scopeKey: session.scopeKey,
    enabled,
    roles,
    scopes: { personal, official },
    onRolePopupOpenChange: handleRolePopupOpenChange,
  };
}

/** 首页不携带项目上下文，直接读取当前空间的个人和官方角色。 */
export function useHomeIpRoles() {
  return useInputIpRoles();
}
